import React, { useEffect, useRef, useState } from "react"
import { useDrop, useDrag } from "react-dnd"
import type { Identifier, XYCoord } from "dnd-core"
import { BadgeElement } from "sharedTypes/badge"
import {
  Box, Flex, HStack, Text,
} from "@chakra-ui/react"
import { useSelection } from "components/CanvasEditor/Editor"
import { getElementDisplayName, getElementIcon } from "./helpers"
import BadgeElementContextMenu from "../../BadgeCanvas/BadgeElementContextMenu"
import { useBadgeInlineEditing } from "../../contexts/BadgeSelectionContext"

type DragItem = {
    index: number
    id: string
    type: string
}

type BadgeLayerItemProps = {
    element: BadgeElement
    onMoveElement: (dragIndex: number, hoverIndex: number) => void
    onCommitReorder: () => void
    index: number
}

/** A layer in the Layers panel; it reads its own place in the selection. */
const BadgeLayerItem = ({
  element,
  onMoveElement,
  onCommitReorder,
  index,
}: BadgeLayerItemProps) => {
  const isSelected = useSelection((state) => state.selectedIds.includes(element.id))
  const { setSelectedElementId } = useBadgeInlineEditing()
  const onSelect = (e: React.MouseEvent) => setSelectedElementId(element.id, e.shiftKey)
  const ref = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const [, setIsHovered] = useState(false)
  const lastPositionRef = useRef<number | null>(null)

  // FLIP animation: track position changes and animate when index changes
  useEffect(() => {
    const node = ref.current
    if (!node) return

    const rect = node.getBoundingClientRect()
    const currentTop = rect.top

    if (
      lastPositionRef.current !== null
        && lastPositionRef.current !== currentTop
    ) {
      const delta = lastPositionRef.current - currentTop

      // Apply inverse transform immediately
      node.style.transform = `translateY(${delta}px)`
      node.style.transition = "none"

      requestAnimationFrame(() => {
        node.style.transform = ""
        node.style.transition = "transform 0.3s cubic-bezier(.215, .61, .355, 1)"
      })
    }

    lastPositionRef.current = currentTop
  }, [index])

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setMenuPos({ x: e.clientX, y: e.clientY })
  }

  // Drop target with hover-based reordering
  const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: Identifier | null }
    >({
      accept: "LAYER_ITEM",
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        }
      },
      hover(item: DragItem, monitor) {
        if (!ref.current) {
          return
        }
        const dragIndex = item.index
        const hoverIndex = index

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return
        }

        const hoverBoundingRect = ref.current?.getBoundingClientRect()
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
        const clientOffset = monitor.getClientOffset()
        const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return
        }
        onMoveElement(dragIndex, hoverIndex)

        // eslint-disable-next-line no-param-reassign
        item.index = hoverIndex
      },
    })

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "LAYER_ITEM",
      item: () => ({ id: element.id, index }),
      end: () => {
        onCommitReorder()
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [element.id, index, onCommitReorder],
  )

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  return (
    <>
      <Flex
        ref={ref}
        opacity={opacity}
        onClick={onSelect}
        data-handler-id={handlerId}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        borderRadius="lg"
        position="relative"
        alignItems="center"
        justifyContent="space-between"
        borderWidth={1}
        w="full"
        h={10}
        px={4}
        backgroundColor={isSelected ? "blue.50" : "white"}
        borderColor={isSelected ? "blue.200" : "gray.200"}
        color={isSelected ? "blue.600" : "gray.700"}
        cursor="pointer"
        boxShadow="sm"
        transition="all 0.2s cubic-bezier(.215, .61, .355, 1)"
        _hover={{
          backgroundColor: isSelected ? "blue.50" : "gray.50",
        }}
      >
        <HStack spacing={3}>
          <Box color={isSelected ? "blue.500" : "gray.600"}>
            {getElementIcon(element.type)}
          </Box>
          <Box>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              lineHeight="normal"
              noOfLines={1}
            >
              {getElementDisplayName(element)}
            </Text>
          </Box>
        </HStack>
      </Flex>
      {menuPos && (
        <BadgeElementContextMenu
          elementId={element.id}
          x={menuPos.x}
          y={menuPos.y}
          onClose={() => setMenuPos(null)}
        />
      )}
    </>
  )
}

export default React.memo(BadgeLayerItem)
